import { createContext, useContext, useState, useEffect } from "react";
import { UserApi } from "../api/user.api";
import { AuthApi } from "../api/auth.api";
import Cookies from "js-cookie";
import { User } from "@/api/types";

interface MeContextType {
  me: User | null;
  loading: boolean;
  fetchMe: () => Promise<void>;
  updateMe: (updatedProperties: Partial<User>) => void;
  logout: () => Promise<void>;
}

const MeContext = createContext<MeContextType | null>(null);

export const MeProvider = ({ children }: { children: React.ReactNode }) => {
  const [me, setMe] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = async () => {
    setLoading(true);
    try {
      const token = Cookies.get("token");
      if (!token) {
        setLoading(false);
        setMe(null);
        return;
      }
      const user = await UserApi.getMe();
      setMe(user ?? null);
    } catch (error) {
      setMe(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMe();
  }, []);

  const updateMe = (updatedProperties: Partial<User>) => {
    setMe((prevMe) => (prevMe ? { ...prevMe, ...updatedProperties } : null));
  };

  const logout = async () => {
    await AuthApi.logout();
    setMe(null);
  };

  return (
    <MeContext.Provider value={{ me, loading, fetchMe, updateMe, logout }}>
      {children}
    </MeContext.Provider>
  );
};

export const useMe = () => {
  const context = useContext(MeContext);
  if (!context) {
    throw new Error("useMe must be used within an MeProvider");
  }
  return context;
};
